import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject
} from '@angular/core';
import {
  ActivationEnd,
  NavigationEnd,
  Event as NavigationEvent,
  Router
} from '@angular/router';
import { each, find, isArray, isObject, isString, map } from 'lodash';
import posthog from 'posthog-js';
import { Observable, Subscription, from, isObservable, of } from 'rxjs';
import { companiesService } from 'src/app/shared/vive-api/companies.service';
import { CompanyDto } from 'src/app/shared/vive-api/vive-api-models';

import { ApConfig, Company, Site, User } from '../classes/models';
import { NavMenuItem } from '../interfaces/nav-menu-item';
import { NavSubMenuItem } from '../interfaces/nav-sub-menu-item';
import { AuthService } from '../services/auth.service';
import { CompanyService } from '../services/company.service';
import { ImpersonationService } from '../services/impersonation.service';
import { NavigationMenuService } from '../services/navigation-menu.service';
import { ToastService } from '../services/toast.service';
import { UtilityService } from '../services/utility.service';

declare type NavConfiguration = {
  [key: string]: NavMenuItem;
};

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NavComponent implements OnInit, OnDestroy {
  private _utilityService = inject(UtilityService);
  private _cdr = inject(ChangeDetectorRef);
  private _impersonationService = inject(ImpersonationService);

  constructor(
    private router: Router,
    private AuthService: AuthService,
    private NavigationMenuService: NavigationMenuService,
    private ToasterService: ToastService,
    private CompanyService: CompanyService
  ) {
    // The router and user subscriptions below both fire off on page load. And the router subscription
    // fires on navigation events as well. Need to have them this way in order to maintain menu on page refresh.
    // This should not cause any race conditions and both subscriptions call the recursivelyCheckMenuLinks
    // function to make sure the menu is displaying correctly.

    // This is used to update the component's ids to undefined after url navigation. This way, we don't
    // hang on to id's that are no longer used. After resetting, we apply any found ids to the component.
    const ResetIds = {
      CompanyId: undefined,
      VendorId: undefined,
      ExemptVendorId: undefined,
      ClientRegisteredVendorId: undefined,
      SiteId: undefined,
      ApConfigId: undefined,
      SiteVendorId: undefined
    };
    const CapturedUrlParams: { [key: string]: any } = {};
    this.router.events.subscribe({
      next: (NavigationEvent: NavigationEvent) => {
        if (NavigationEvent instanceof ActivationEnd) {
          const {
            snapshot: { params }
          } = NavigationEvent;
          Object.assign(CapturedUrlParams, params);
        }

        if (NavigationEvent instanceof NavigationEnd) {
          const { url } = NavigationEvent;
          this.currentUrl = url;

          Object.assign(this, CapturedUrlParams);
          Object.assign(CapturedUrlParams, ResetIds);

          this.recursivelyCheckMenuLinks(
            Array.from(this.navMenuItems.values()),
            true
          );
          this._cdr.markForCheck();
        }
      }
    });

    this.constructNavMenu();
  }

  navMenuItems = new Map<string, NavMenuItem>();
  // Represents the current url the user has navigated to.
  currentUrl?: string;

  SelectedMenuItem?: NavMenuItem;

  getUserInfo!: Subscription;
  routerEvent!: Subscription;
  NavMenuServiceSubscription!: Subscription;

  user: any;
  payload: any;

  showNav: boolean = false;
  @Input() multiNavIsCollapsed: boolean = false;
  @Output() multiNavIsCollapsedChange = new EventEmitter<boolean>();

  @Input() wholeNavIsCollapsed: boolean = false;
  @Output() wholeNavIsCollapsedChange = new EventEmitter<boolean>();

  toggleElement!: HTMLElement;
  mobileNavIsCollapsed: boolean = false;
  bootstrap_md_breakPoint: number = 768; // This number is the default bootstrap v5 md breakpoint. If we ever change the default we should change this to keep breakpoints in sync.
  mobileMode: boolean = window.innerWidth <= this.bootstrap_md_breakPoint;

  currentVendorId: {
    VendorId?: number | null;
    ExemptVendorId?: number | null;
    ClientRegisteredVendorId?: number | null;
  } = { VendorId: null, ExemptVendorId: null, ClientRegisteredVendorId: null };
  currentSite: Site = {};
  currentClient?: Company | null;

  // Utilize an array to store collapsed items when the whole menu is collapsed. When the menu is uncollapsed,
  // retrieve the stored items and uncollapse them.
  StoredNavMenuItemCollapseState: NavMenuItem[] = [];

  CompanyId?: number;
  VendorId?: number;
  ExemptVendorId?: number;
  ClientRegisteredVendorId?: number;
  SiteVendorId?: number;
  SiteId?: number;
  ApConfigId?: number;
  ApConfigs?: ApConfig[];

  showPublicApiKeys: boolean = false;

  preserveMapOrder = this._utilityService.keyValuePairDefaultOrder;

  constructNavMenu() {
    this.user = this.AuthService.getUser();

    const allowedCompanyIds = (posthog.getFeatureFlagPayload(
      'public-api-keys'
    ) ?? []) as string[];
    this.showPublicApiKeys =
      posthog.isFeatureEnabled('public-api-keys') === true &&
      allowedCompanyIds.includes(String(this.user?.CompanyId));

    const { CompanyId } = this.user;

    ((): Observable<CompanyDto | null> => {
      if (CompanyId) {
        return from(
          companiesService.findOneById({
            pathParams: { companyId: CompanyId },
            queryParams: {
              includeAssociations: [
                {
                  name: 'ApConfigs'
                }
              ]
            }
          })
        );
      }

      return of(null);
    })().subscribe({
      next: (FoundCompany) => {
        if (FoundCompany) {
          Object.assign(this.user, { Company: FoundCompany });
        }

        const { UserAccountType, UserAccountRole, Company } = this.user;

        if (!UserAccountType || !UserAccountRole) {
          throw this.ToasterService.addErrorToast(
            'Error',
            'User is missing account information.',
            { returnError: true }
          );
        }

        const { role } = UserAccountRole;
        const { name } = UserAccountType;

        if (name === 'Client' && Company) {
          const { ApConfigs } = Company;

          this.ApConfigs = ApConfigs;
        }

        if (!name) {
          throw this.ToasterService.addErrorToast(
            'Error',
            'User account is missing permission.',
            { returnError: true }
          );
        }

        const UserTypeToNavConfigurationMap: {
          Admin?: NavConfiguration;
          Vendor?: NavConfiguration;
          Client?: NavConfiguration;
        } = {
          Admin: {
            DASHBOARD: {
              name: 'Dashboard',
              link: ['admin', 'dashboard'],
              icon: 'bi-speedometer'
            },
            ['VENDOR PROFILES']: {
              name: 'Vendor profiles',
              link: ['admin', 'vendors'],
              icon: 'bi-wrench',
              subMenuLink: () => {
                const urlParts: (string | number | undefined)[] = [];

                if (this.VendorId) {
                  urlParts.push('admin', 'vendor', this.VendorId);
                }

                if (this.ExemptVendorId) {
                  urlParts.push('admin', 'exempt-vendor', this.ExemptVendorId);
                }

                if (this.ClientRegisteredVendorId) {
                  urlParts.push(
                    'admin',
                    'client-registered-vendor',
                    this.ClientRegisteredVendorId
                  );
                }

                return [urlParts];
              },
              subMenuFn: () => {
                if (this.VendorId) {
                  return {
                    menuItems: [
                      {
                        name: 'Info',
                        link: ['admin', 'vendor', this.VendorId, 'info']
                      },
                      {
                        name: 'Contacts',
                        link: ['admin', 'vendor', this.VendorId, 'contacts']
                      },
                      {
                        name: 'Clients',
                        link: ['admin', 'vendor', this.VendorId, 'clients']
                      },
                      {
                        name: 'Owners',
                        link: ['admin', 'vendor', this.VendorId, 'owners']
                      },
                      {
                        name: 'Invoices',
                        link: ['admin', 'vendor', this.VendorId, 'invoices']
                      },
                      {
                        name: 'Documents',
                        link: ['admin', 'vendor', this.VendorId, 'documents']
                      },
                      {
                        name: 'Notes',
                        link: ['admin', 'vendor', this.VendorId, 'notes']
                      },
                      {
                        name: 'Admin',
                        link: ['admin', 'vendor', this.VendorId, 'admin']
                      }
                    ]
                  };
                }

                if (this.ExemptVendorId) {
                  return {
                    menuItems: [
                      {
                        name: 'Info',
                        link: [
                          'admin',
                          'exempt-vendor',
                          this.ExemptVendorId,
                          'info'
                        ]
                      },
                      {
                        name: 'Contacts',
                        link: [
                          'admin',
                          'exempt-vendor',
                          this.ExemptVendorId,
                          'contacts'
                        ]
                      },
                      {
                        name: 'Admin',
                        link: [
                          'admin',
                          'exempt-vendor',
                          this.ExemptVendorId,
                          'admin'
                        ]
                      }
                    ]
                  };
                }

                if (this.ClientRegisteredVendorId) {
                  return {
                    menuItems: [
                      {
                        name: 'Info',
                        link: [
                          'admin',
                          'client-registered-vendor',
                          this.ClientRegisteredVendorId,
                          'info'
                        ]
                      },
                      {
                        name: 'Contacts',
                        link: [
                          'admin',
                          'client-registered-vendor',
                          this.ClientRegisteredVendorId,
                          'contacts'
                        ]
                      },
                      {
                        name: 'Admin',
                        link: [
                          'admin',
                          'client-registered-vendor',
                          this.ClientRegisteredVendorId,
                          'admin'
                        ]
                      }
                    ]
                  };
                }

                return undefined;
              }
            },
            CLIENTS: {
              name: 'Clients',
              link: ['admin', 'clients'],
              icon: 'bi-people',
              isCollapsed: true,
              subMenuLink: () => [['admin', 'client', this.CompanyId]],
              subMenuFn: () => {
                if (this.CompanyId) {
                  return {
                    menuItems: [
                      {
                        name: 'Client info',
                        link: ['admin', 'client', this.CompanyId, 'clientInfo']
                      },
                      {
                        name: 'Compliance',
                        link: [
                          'admin',
                          'client',
                          this.CompanyId,
                          'complianceSettings'
                        ]
                      },
                      {
                        name: 'AP configurations',
                        link: ['admin', 'client', this.CompanyId, 'ap-configs']
                      },
                      {
                        name: 'Notes',
                        link: ['admin', 'client', this.CompanyId, 'notes']
                      },
                      {
                        name: 'Sites',
                        link: ['admin', 'client', this.CompanyId, 'sites']
                      },
                      {
                        name: 'Offices',
                        link: [
                          'admin',
                          'client',
                          this.CompanyId,
                          'service-regions'
                        ]
                      },
                      {
                        name: 'Vendors',
                        link: ['admin', 'client', this.CompanyId, 'vendors']
                      },
                      {
                        name: 'Admin',
                        link: ['admin', 'client', this.CompanyId, 'admin']
                      }
                    ]
                  };
                }

                return undefined;
              }
            },
            'VIVE VETTING': {
              name: 'VIVE Vetting',
              icon: 'bi-clipboard-check',
              isCollapsed: true,
              link: ['admin', 'run-vive', { viveStatus: '' }],
              subMenuLink: () => [['admin', 'run-vive']],
              subMenuFn: () => {
                const menuItems = [
                  { status: '', display: 'All' },
                  { status: 'Registering', display: 'Registering' },
                  { status: 'Awaiting Payment', display: 'Awaiting payment' },
                  { status: 'Registered', display: 'Registered' },
                  { status: 'Renewing', display: 'Renewing' },
                  { status: 'Reviewing', display: 'Reviewing' },
                  { status: 'Suspended', display: 'Suspended' },
                  { status: 'Inactive', display: 'Inactive' },
                  { status: 'Business Verified', display: 'Business verified' },
                  { status: 'Non-Verified', display: 'Non-verified' },
                  { status: 'Compliance Exempt', display: 'Compliance exempt' },
                  { status: 'Approved', display: 'Approved' },
                  { status: 'Site Conditional', display: 'Site conditional' }
                ];

                const sortedMenuItems = menuItems.sort((a, b) => {
                  if (a.status === '') return -1;
                  if (b.status === '') return 1;
                  return a.status.localeCompare(b.status);
                });

                return {
                  menuItems: map(sortedMenuItems, (displayObject) => ({
                    name: `${displayObject.display} vendors`,
                    link: [
                      'admin',
                      'run-vive',
                      { viveStatus: displayObject.status }
                    ],
                    // linkTemplate: '/admin/run-vive/',
                    // Need to review the constructed link in the browser
                    linkTemplate: '',
                    isCollapsed: true
                  }))
                };
              }
            },
            REPORTS: {
              name: 'Reports',
              link: ['admin', 'reports'],
              icon: 'bi-bar-chart-line'
            },
            DASHBOARDS: {
              name: 'BI Dashboards',
              link: ['admin', 'dashboards'],
              icon: 'bi-clipboard-data',
              badgeLabel: 'VIVE Labs',
              badgeIcon: 'bi-flask-fill',
              badgeClassName: 'subNavMenu-item--badge-orange',
              hasUserAccountRolePrivilege: [
                'ADMIN_ADMIN',
                'ADMIN_PRIME_TEAM',
                'ADMIN_CUSTOMER_SERVICE_TEAM',
                'ADMIN_PROJECT_MANAGEMENT_TEAM',
                'ADMIN_FRAUD_TEAM',
                'ADMIN_INSURANCE_TEAM',
                'ADMIN_EMAILS_TEAM'
              ]
            },
            ADMIN: {
              name: 'Admin',
              link: 'preventNavigation',
              icon: 'bi-gear',
              isCollapsed: true,
              subMenuLink: () => [['admin', 'admin']],
              subMenu: {
                menuItems: [
                  {
                    name: 'Users',
                    link: ['admin', 'admin', 'users'],
                    icon: 'bi-people'
                  },
                  {
                    name: 'Trades',
                    link: ['admin', 'admin', 'trades']
                  },
                  {
                    name: 'Change log',
                    link: ['admin', 'admin', 'change-log']
                  },
                  {
                    name: 'Client AP configs',
                    link: ['admin', 'admin', 'client-ap-configs']
                  },
                  {
                    name: 'Esign templates',
                    link: ['admin', 'admin', 'esign-templates'],
                    hasUserAccountRolePrivilege: [
                      'ADMIN_ADMIN',
                      'ADMIN_PROJECT_MANAGEMENT_TEAM'
                    ]
                  },
                  {
                    name: 'SSO connections',
                    link: ['admin', 'admin', 'sso-connections'],
                    // The SSO admin API admits ADMIN_ADMIN only.
                    hasUserAccountRolePrivilege: ['ADMIN_ADMIN']
                  },
                  {
                    name: 'Emails',
                    link: ['admin', 'admin', 'emails'],
                    badgeLabel: 'VIVE Labs',
                    badgeIcon: 'bi-flask-fill',
                    badgeClassName: 'subNavMenu-item--badge-orange'
                  }
                ]
              }
            },
            HELP: {
              name: 'Help',
              icon: 'bi-question-circle',
              isCollapsed: true,
              subMenu: {
                menuItems: [
                  {
                    name: 'Vendor help center',
                    link: 'https://support.getvived.com/hc/en-us/categories/29859212679323-Vendor-Help-Center-2-0',
                    isExternalLink: true
                  },
                  {
                    name: 'Client help center',
                    link: 'https://support.getvived.com/hc/en-us/categories/30093719657883-Client-Help-Center-2-0',
                    isExternalLink: true
                  }
                ]
              }
            },
            LOGOUT: {
              name: 'Logout',
              link: '/logout',
              icon: 'bi-box-arrow-right'
            }
          },
          Vendor: {
            QUICKSOURCE: {
              name: 'QuickSourceBids',
              link: ['vendor', this.user.VendorId, 'proposals'],
              icon: 'bi-clipboard-check'
            },
            LOGOUT: {
              name: 'Logout',
              link: '/logout',
              icon: 'bi-box-arrow-right'
            }
          },
          Client: {
            DASHBOARD: {
              name: 'Dashboard',
              link: ['client', this.user.CompanyId, 'dashboard'],
              icon: 'bi-speedometer'
            },
            'MY VENDORS': {
              name: 'My vendors',
              link: (() => {
                const NavArray: string[] = [
                  'client',
                  this.user.CompanyId,
                  'vendors'
                ];

                if (role !== 'CLIENT_ADMIN') {
                  NavArray.push('search');
                }

                NavArray.push('client');

                return NavArray;
              })(),
              icon: 'bi-wrench',
              isCollapsed: true,
              subMenuLink: () => {
                const urlParts: (string | number)[] = [
                  'client',
                  this.AuthService.getUserCompanyId(),
                  'vendors',
                  'my-vendor'
                ];
                if (this.VendorId) {
                  urlParts.push(this.VendorId);
                } else if (this.ExemptVendorId) {
                  urlParts.push('exempt-vendor', this.ExemptVendorId);
                } else if (this.ClientRegisteredVendorId) {
                  urlParts.push(
                    'client-registered-vendor',
                    this.ClientRegisteredVendorId
                  );
                }
                return [urlParts];
              },
              subMenuFn: () => {
                const urlParts: (string | number)[] = [
                  'client',
                  this.AuthService.getUserCompanyId(),
                  'vendors',
                  'my-vendor'
                ];
                const isAdmin = new Set([
                  'ADMIN_ADMIN',
                  'ADMIN_PROJECT_MANAGEMENT_TEAM',

                  'CLIENT_ADMIN'
                ]).has(role);
                if (this.VendorId) {
                  urlParts.push(this.VendorId);

                  return {
                    menuItems: [
                      { name: 'Info', link: [...urlParts, 'info'] },
                      { name: 'Contacts', link: [...urlParts, 'contacts'] },
                      { name: 'Insurance', link: [...urlParts, 'insurance'] },
                      {
                        name: 'Trades & licenses',
                        link: [...urlParts, 'trades-and-licenses']
                      },
                      { name: 'QuickSourceBids', link: [...urlParts, 'qsb'] },
                      { name: 'Contracts', link: [...urlParts, 'contracts'] },
                      { name: 'Ratings', link: [...urlParts, 'ratings'] },
                      { name: 'Sites', link: [...urlParts, 'sites'] },
                      { name: 'History', link: [...urlParts, 'history'] },
                      { name: 'Documents', link: [...urlParts, 'documents'] },
                      {
                        name: 'Admin',
                        link: [...urlParts, 'admin'],
                        hide: !isAdmin
                      }
                    ]
                  };
                }

                if (this.ExemptVendorId) {
                  urlParts.push('exempt-vendor', this.ExemptVendorId);

                  return {
                    menuItems: [
                      { name: 'Info', link: [...urlParts, 'info'] },
                      { name: 'Contacts', link: [...urlParts, 'contacts'] },
                      { name: 'Ratings', link: [...urlParts, 'ratings'] },
                      { name: 'Sites', link: [...urlParts, 'sites'] },
                      { name: 'History', link: [...urlParts, 'history'] },
                      { name: 'Documents', link: [...urlParts, 'documents'] },
                      {
                        name: 'Admin',
                        link: [...urlParts, 'admin'],
                        hide: !isAdmin
                      }
                    ]
                  };
                }

                if (this.ClientRegisteredVendorId) {
                  urlParts.push(
                    'client-registered-vendor',
                    this.ClientRegisteredVendorId
                  );

                  return {
                    menuItems: [
                      { name: 'Info', link: [...urlParts, 'info'] },
                      { name: 'Contacts', link: [...urlParts, 'contacts'] },
                      { name: 'Ratings', link: [...urlParts, 'ratings'] },
                      { name: 'Sites', link: [...urlParts, 'sites'] },
                      { name: 'History', link: [...urlParts, 'history'] },
                      { name: 'Documents', link: [...urlParts, 'documents'] },
                      {
                        name: 'Admin',
                        link: [...urlParts, 'admin'],
                        hide: !isAdmin
                      }
                    ]
                  };
                }

                return undefined;
              }
            },
            'FIND NEW VENDORS': {
              name: 'Find new vendors',
              link: (() => {
                const NavArray: string[] = [
                  'client',
                  this.user.CompanyId,
                  'vendors'
                ];

                if (role !== 'CLIENT_ADMIN') {
                  NavArray.push('search');
                }

                NavArray.push('vive');

                return NavArray;
              })(),
              icon: 'magnifying-glass',
              hide: Company?.verifyClientRegisteredVendorBusinessOnly,
              isCollapsed: true,
              subMenuLink: () => {
                return [
                  [
                    'client',
                    this.AuthService.getUserCompanyId(),
                    'vendors',
                    'vive-vendor',
                    this.VendorId
                  ]
                ];
              },
              subMenuFn: () => {
                if (this.VendorId) {
                  return {
                    menuItems: [
                      {
                        name: 'VIVE Vendor',
                        link: [
                          'client',
                          this.AuthService.getUserCompanyId(),
                          'vendors',
                          'vive-vendor',
                          this.VendorId
                        ]
                      }
                    ]
                  };
                }

                return undefined;
              }
            },
            QUICKSOURCE: {
              name: 'QuickSourceBids',
              link: ['client', this.user.CompanyId, 'proposal-requests'],
              icon: 'bi-clipboard-check',
              hide: Company?.verifyClientRegisteredVendorBusinessOnly
            },
            CONTRACTS: {
              name: 'Contracts',
              link: ['client', this.user.CompanyId, 'contracts'],
              icon: 'bi-cash-coin',
              hide: Company?.verifyClientRegisteredVendorBusinessOnly
            },
            RATINGS: {
              name: 'Ratings',
              link: ['client', this.user.CompanyId, 'vendor-ratings'],
              icon: 'bi bi-star',
              hide: Company?.verifyClientRegisteredVendorBusinessOnly
            },
            SITES: {
              // pluralization...
              // Consider creating a partial route for the subMenu without the required id.
              name: 'Sites',
              link: ['client', this.user.CompanyId, 'sites'],
              icon: 'bi-buildings',
              isCollapsed: true,
              hide: Company?.verifyClientRegisteredVendorBusinessOnly,
              subMenuLink: () => [
                ['client', this.user.CompanyId, 'site', this.SiteId]
              ],
              subMenuFn: () => {
                if (this.SiteId) {
                  const menuItems: NavMenuItem[] = [
                    {
                      name: 'Overview',
                      link: [
                        'client',
                        this.user.CompanyId,
                        'site',
                        this.SiteId,
                        'siteDetails'
                      ]
                    },
                    // No Site Contacts for now.
                    // {
                    //   name: 'Contacts',
                    //   link: ['client', this.user.CompanyId, 'site', this.SiteId, 'contacts']
                    // },
                    {
                      name: 'Vendors',
                      link: [
                        'client',
                        this.user.CompanyId,
                        'site',
                        this.SiteId,
                        'vendors'
                      ],
                      isCollapsed: true,
                      subMenuLink: () => [
                        [
                          'client',
                          this.user.CompanyId,
                          'site',
                          this.SiteId,
                          'vendor'
                        ]
                      ],
                      subMenuFn: () => {
                        if (this.SiteVendorId) {
                          const menuItems: NavMenuItem[] = [
                            {
                              name: 'Files',
                              link: [
                                'client',
                                this.user.CompanyId,
                                'site',
                                this.SiteId,
                                'vendor',
                                this.SiteVendorId,
                                'files'
                              ]
                            }
                          ];

                          return { menuItems };
                        }

                        return undefined;
                      }
                    },
                    {
                      name: 'Users',
                      link: [
                        'client',
                        this.user.CompanyId,
                        'site',
                        this.SiteId,
                        'users'
                      ]
                    },
                    {
                      name: 'Files',
                      link: [
                        'client',
                        this.user.CompanyId,
                        'site',
                        this.SiteId,
                        'files'
                      ]
                    }
                    // {
                    //   name: 'Admin',
                    //   link: ['client', this.user.CompanyId, 'site', this.SiteId, 'admin']
                    // }
                  ];

                  if (this.user.Company?.allowAdditionalInsuredEndorsements) {
                    menuItems.splice(3, 0, {
                      name: 'Vendors validated',
                      link: [
                        'client',
                        this.user.CompanyId,
                        'site',
                        this.SiteId,
                        'vendors-validated'
                      ]
                    });
                  }

                  return { menuItems };
                }
                return undefined;
              }
            },
            REPORTS: {
              name: 'Reports',
              link: ['client', this.user.CompanyId, 'reports'],
              icon: 'bi-bar-chart-line'
            },
            ADMIN: {
              name: 'Admin',
              icon: 'bi-gear',
              // May need to correct this.
              link: 'preventNavigation',
              subMenuLink: () => [['client', this.user.CompanyId, 'admin']],
              hide: UserAccountRole.role !== 'CLIENT_ADMIN',
              isCollapsed: true,
              subMenu: {
                menuItems: [
                  {
                    name: 'Company',
                    link: ['client', this.user.CompanyId, 'admin', 'my-company']
                  },
                  {
                    name: 'Users',
                    link: ['client', this.user.CompanyId, 'admin', 'users'],
                    icon: 'bi-people'
                  },
                  {
                    name: 'Spotlight vendors',
                    link: [
                      'client',
                      this.user.CompanyId,
                      'admin',
                      'spotlight-vendors'
                    ],
                    hide: Company?.verifyClientRegisteredVendorBusinessOnly
                  },
                  {
                    name: 'Compliance exempt setup',
                    link: [
                      'client',
                      this.user.CompanyId,
                      'admin',
                      'compliance-exempt-setup'
                    ],
                    hide: Company?.verifyClientRegisteredVendorBusinessOnly
                  },
                  {
                    name: 'API keys',
                    link: [
                      'client',
                      this.user.CompanyId,
                      'admin',
                      'public-api-keys'
                    ],
                    hide: !this.showPublicApiKeys
                  },
                  {
                    name: 'Accounting',
                    hide:
                      Company?.verifyClientRegisteredVendorBusinessOnly ||
                      (find(this.ApConfigs, ['apSystem', 'eUnify']) &&
                        this.ApConfigs?.length === 1),
                    link: (() => {
                      const linkArray = [
                        'client',
                        this.user.CompanyId,
                        'admin',
                        'accounting'
                      ];

                      const apConfig = this.ApConfigs?.[0];
                      if (this.ApConfigs?.length === 1 && apConfig?.id) {
                        linkArray.push(apConfig.id);
                      }

                      return linkArray;
                    })()
                  }
                ]
              }
            },
            HELP: {
              name: 'Help',
              icon: 'bi-question-circle',
              isCollapsed: true,
              subMenu: {
                menuItems: [
                  {
                    name: 'Help center',
                    link: 'https://support.getvived.com/hc/en-us/categories/30093719657883-Client-Help-Center-2-0',
                    isExternalLink: true
                  },
                  {
                    name: 'Chat with VIVE',
                    linkFn: () => {
                      window.$zopim.livechat.window.show();
                    }
                  }
                ]
              }
            },
            // `/logout` performs the Return while impersonating.
            LOGOUT: this._impersonationService.isImpersonating()
              ? {
                  name: 'Return to my account',
                  link: ['logout'],
                  icon: 'bi-arrow-return-left'
                }
              : {
                  name: 'Logout',
                  link: ['logout'],
                  icon: 'bi-box-arrow-right'
                }
          }
        };

        const isPermittedForRole = ({
          hasUserAccountRolePrivilege
        }: NavMenuItem): boolean =>
          !hasUserAccountRolePrivilege ||
          hasUserAccountRolePrivilege.includes(role!);

        // Sub-menu entries carry the same privilege list as top-level ones, but the
        // sub-menu template honours only `hide` — so the prune has to walk into them
        // rather than leaving a forbidden entry rendered.
        const pruneSubMenuByRole = (menuItem: NavMenuItem): void => {
          const { subMenu } = menuItem;

          if (!subMenu) {
            return;
          }

          subMenu.menuItems = subMenu.menuItems.filter(isPermittedForRole);
          subMenu.menuItems.forEach(pruneSubMenuByRole);
        };

        Object.values(UserTypeToNavConfigurationMap).forEach(
          (value: NavConfiguration) => {
            const currentNavConfigurationMenu = value;

            Object.entries(currentNavConfigurationMenu).forEach(
              ([key, menuItems]) => {
                if (!isPermittedForRole(menuItems)) {
                  delete currentNavConfigurationMenu[key];
                  return;
                }

                pruneSubMenuByRole(menuItems);
              }
            );
          }
        );

        const NavConfiguration =
          UserTypeToNavConfigurationMap[name as 'Client' | 'Vendor' | 'Admin'];

        if (!NavConfiguration) {
          throw this.ToasterService.addErrorToast(
            'Error',
            'Missing Navigation Menu configuration.',
            { returnError: true }
          );
        }

        // Prevent any permission role not in this set from using reports.
        // Backend updates have also been made.
        if (
          !new Set([
            'ADMIN_ADMIN',
            'ADMIN_PROJECT_MANAGEMENT_TEAM',
            'CLIENT_ADMIN',
            'CLIENT_VENDOR_AND_SITES_MANAGER',
            'VENDOR_ADMIN'
          ]).has(role)
        ) {
          delete NavConfiguration['REPORTS'];
        }

        this.navMenuItems.clear();

        Object.keys(NavConfiguration).forEach((NavKey) => {
          this.navMenuItems.set(NavKey, NavConfiguration[NavKey]);
        });

        this.recursivelyCheckMenuLinks(
          Array.from(this.navMenuItems.values()),
          true
        );
        this._cdr.markForCheck();
      }
    });
  }

  toggleCollapse() {
    this.mobileMode
      ? (this.wholeNavIsCollapsed = !this.wholeNavIsCollapsed)
      : (this.multiNavIsCollapsed = !this.multiNavIsCollapsed);

    this.wholeNavIsCollapsedChange.emit(this.wholeNavIsCollapsed);
    this.multiNavIsCollapsedChange.emit(this.multiNavIsCollapsed);

    if (this.wholeNavIsCollapsed || this.multiNavIsCollapsed) {
      this.navMenuItems.forEach((Item) => {
        const { isCollapsed } = Item;

        if (!isCollapsed) {
          this.StoredNavMenuItemCollapseState.push(Item);
        }

        Object.assign(Item, { isCollapsed: true });
      });
    } else {
      this.StoredNavMenuItemCollapseState.forEach((Item) => {
        Object.assign(Item, { isCollapsed: false });
      });
      this.StoredNavMenuItemCollapseState.splice(0);
    }
  }

  menuClicked(item: NavMenuItem) {
    const {
      link,
      linkFn,
      subMenu,
      isCollapsed,
      isExternalLink,
      isUnSelectable
    } = item;

    if (!isUnSelectable) {
      if (link) {
        if (link === 'preventNavigation') {
          // Prevent Navigation
          this.recursivelyCheckMenuLinks(
            Array.from(this.navMenuItems.values()),
            true
          );
        } else if (isExternalLink) {
          if (typeof link !== 'string') {
            throw new Error('Link is not a string.');
          }
          window.open(link, '_blank');
        } else if (typeof link === 'string') {
          this.router.navigateByUrl(link);
        } else if (Array.isArray(link)) {
          this.router.navigate(link);
        }
      }

      if (linkFn) {
        linkFn();
      }

      if (subMenu) {
        Object.assign(item, { isCollapsed: !isCollapsed });
      }
    }
  }

  resetMenuItems() {
    this.navMenuItems.forEach((item) => (item.isCollapsed = true));
  }

  getUserRoute(User: User) {
    const { UserAccountType } = User;
    if (!UserAccountType) {
      throw this.ToasterService.addErrorToast(
        'Error',
        'Missing User Account Type from User.',
        { returnError: true }
      );
    }

    const { name } = UserAccountType;

    const AccountTypeToRouteMap = {
      Admin: 'admin',
      Vendor: 'vendor',
      Client: 'client',
      'External Vive Level 1': 'admin',
      'External Vive Level 2': 'admin'
    };

    return AccountTypeToRouteMap[name];
  }

  handleMenuSelectionState(NavMenuItem: NavMenuItem) {
    if (this.SelectedMenuItem) {
      Object.assign(this.SelectedMenuItem, {
        isSelected: false,
        isCollapsed: true
      });
      const { subMenu, subMenuFn } = this.SelectedMenuItem;
      if (subMenu && subMenuFn) {
        delete this.SelectedMenuItem.subMenu;
      }
    }
    this.SelectedMenuItem = Object.assign(NavMenuItem, { isSelected: true });
  }

  handleLinkSelection(
    NavMenuItem: NavMenuItem,
    link: string | (string | number | undefined | { [key: string]: any })[]
  ) {
    if (this.currentUrl) {
      const { subMenuLink } = NavMenuItem;
      const currentUrlArrayParts = this.currentUrl.split('/');
      const modifiedCurrentUrlArrayParts: string[] = [];
      currentUrlArrayParts.forEach((currentUrlArrayPart) => {
        const urlOptions = currentUrlArrayPart.split(';');
        modifiedCurrentUrlArrayParts.push(...urlOptions);
      });

      modifiedCurrentUrlArrayParts.shift();
      let linkParts: (string | number | undefined)[] | undefined;
      if (isArray(link)) {
        linkParts = map(link, (linkItem) => {
          if (isObject(linkItem)) {
            const linkParts: string[] = [];
            each(linkItem, (value, key) => {
              linkParts.push(key, '=', encodeURIComponent(value));
            });
            return linkParts.join('');
          } else {
            return linkItem;
          }
        });
      }

      if (isString(link)) {
        linkParts = link.split('/');
      }

      if (linkParts && modifiedCurrentUrlArrayParts) {
        const doesUrlMatchLinkUrl =
          modifiedCurrentUrlArrayParts.length > 0 &&
          linkParts.length > 0 &&
          linkParts.every((part, index) => {
            return (
              (part || '').toString() === modifiedCurrentUrlArrayParts[index]
            );
          });

        let doesUrlMatchSubMenuLinkUrl: boolean = false;

        if (subMenuLink) {
          const linkPartsArray = subMenuLink();
          linkPartsArray.forEach((linkParts) => {
            if (
              modifiedCurrentUrlArrayParts.length > 0 &&
              linkParts.length > 0 &&
              linkParts.every((part, index) => {
                return (
                  (part || '').toString() ===
                  modifiedCurrentUrlArrayParts[index]
                );
              })
            ) {
              doesUrlMatchSubMenuLinkUrl = true;
            }
          });
        }

        if (doesUrlMatchLinkUrl || doesUrlMatchSubMenuLinkUrl) {
          Object.assign(NavMenuItem, { isSelected: true });
        } else {
          Object.assign(NavMenuItem, { isSelected: false });
        }
      }

      const { isSelected } = NavMenuItem;

      return isSelected;
    }
    return false;
  }

  processSubMenu(
    NavMenuItem: NavMenuItem,
    subMenu: NavSubMenuItem,
    subMenuLinkFn: (() => (string | number | undefined)[][]) | undefined
  ) {
    if (subMenu) {
      if (!this.wholeNavIsCollapsed && !this.multiNavIsCollapsed) {
        Object.assign(NavMenuItem, { isCollapsed: false });
      } else {
        // this.StoredNavMenuItemCollapseState.push(nmi);
        // 2/26/2025 don't re-open nav menu when changing routes.
        // Object.assign(this, { wholeNavIsCollapsed: false, multiNavIsCollapsed: false });
        // Object.assign(nmi, { isCollapsed: false });

        this.StoredNavMenuItemCollapseState.push(
          Object.assign(NavMenuItem, { isCollapsed: true })
        );
      }

      if (subMenuLinkFn) {
        const subMenuLinks = subMenuLinkFn();

        subMenuLinks.forEach((subMenuLink) => {
          this.handleLinkSelection(NavMenuItem, subMenuLink);
        });
      }

      const { menuItems } = subMenu;

      this.recursivelyCheckMenuLinks(menuItems, false);
    }
  }

  // Utilize this function to determine if a menu/sub-menu item is supposed to be displayed based
  // on the current URL. This is important if someone refreshes the page. We need to show what
  // menu they are currently viewing.
  recursivelyCheckMenuLinks(
    NavMenuItems: NavMenuItem[],
    isFirstRecursion: boolean
  ) {
    if (this.currentUrl) {
      NavMenuItems.forEach((nmi) => {
        let { link } = nmi;

        if (link) {
          this.handleLinkSelection(nmi, link);
        }

        const { isSelected, subMenuFn, subMenuLink: subMenuLinkFn } = nmi;

        if (isSelected) {
          if (isFirstRecursion) {
            this.handleMenuSelectionState(nmi);
          }

          if (subMenuFn && !nmi.subMenu) {
            const subMenu = subMenuFn();
            if (subMenu) {
              (() =>
                isObservable(subMenu) ? subMenu : of(subMenu))().subscribe({
                next: (subMenu) => {
                  Object.assign(nmi, { subMenu });
                  this.processSubMenu(nmi, subMenu, subMenuLinkFn);
                  this._cdr.markForCheck();
                }
              });
            }
          } else if (nmi.subMenu) {
            // VPDW-3111: static-defined subMenus (client Admin, admin
            // Admin, Help, etc.) weren't running through processSubMenu
            // so they stayed collapsed even when the URL matched a
            // child. Run it here so the parent expands and the matching
            // child gets `isSelected: true` for active highlighting.
            this.processSubMenu(nmi, nmi.subMenu, subMenuLinkFn);
          }
        }
      });
    }
  }

  ngOnInit(): void {
    this.showNav = true;

    this.NavMenuServiceSubscription = this.NavigationMenuService.listenForEvent<
      | Company
      | {
          VendorId?: number;
          ExemptVendorId?: number;
          ClientRegisteredVendorId?: number;
        }
      | Site
      | null
      | undefined
    >().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'toggleNavMenu') {
          this.toggleCollapse();
        }

        if (eventName === 'reconstructNavMenu') {
          this.constructNavMenu();
        }
        this._cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.getUserInfo) {
      this.getUserInfo.unsubscribe();
    }

    if (this.routerEvent) {
      this.routerEvent.unsubscribe();
    }

    if (this.NavMenuServiceSubscription) {
      this.NavMenuServiceSubscription.unsubscribe();
    }
  }
}
